import { LitElement, html, nothing, type TemplateResult } from "lit";
import { customElement, state } from "lit/decorators.js";
import {
  initServices,
  getServices,
  type Services,
  type UserProfile,
  type CreditBalance,
  type GatewayConnectionState,
  type ChatMessage,
  type ConversationMode,
  type SubscriptionInfo,
  type SubAgentRun,
  type CanvasEvent,
} from "../services/index.ts";
import type { SkillSlashCommand } from "../views/chat.ts";
import { directRestRequest, getSessionDirect } from "../services/supabase-client.ts";
import { getModelForTier, saveTierPreferences, setCustomModels, getCustomModels, clearCustomModels, loadToolPreferences, saveToolPreferences, type ModelEntry, type TierPreferences, type ToolPreferences } from "../models.ts";
import {
  type ConsumerView,
  TABS,
  MOBILE_TABS,
  SIDEBAR_ONLY_TABS,
  getViewFromHash,
  setViewHash,
} from "./consumer-navigation.ts";

// Import views
import "../views/landing.ts";
import "../views/onboarding.ts";
import "../views/chat.ts";
import "../views/channels.ts";
import "../views/settings.ts";
import "../views/sessions.ts";
import "../views/usage.ts";
import "../views/automations.ts";
import "../views/skills.ts";
import "../views/devices.ts";
import "../views/integrations.ts";
import "../views/download.ts";
import "../views/terms.ts";
import "../views/billing-modal.ts";
import "../views/model-browser.ts";

// Import components
import "../components/credit-indicator.ts";
import type { KatanoidCanvasPanel } from "../components/canvas-panel.ts";

// Credit helpers
function getRemaining(b: CreditBalance): number {
  return b.monthlyCredits - b.monthlyUsed + b.bonusCredits;
}
function getTotal(b: CreditBalance): number {
  return b.monthlyCredits + b.bonusCredits;
}
function getCreditLevel(b: CreditBalance): "high" | "medium" | "low" {
  const pct = getTotal(b) > 0 ? (getRemaining(b) / getTotal(b)) * 100 : 0;
  if (pct > 40) return "high";
  if (pct > 20) return "medium";
  return "low";
}

type OnboardingStep = "signup" | "login" | "terms" | "privacy";
type ThemeMode = "light" | "dark" | "system";

/** Map pathname to onboarding step (pre-auth pages) */
function getOnboardingFromPath(): OnboardingStep | null {
  switch (window.location.pathname) {
    case "/signup": return "signup";
    case "/login": return "login";
    case "/terms": return "terms";
    case "/privacy": return "privacy";
    default: return null;
  }
}

function navigateTo(path: string): void {
  window.history.pushState(null, "", path);
  window.dispatchEvent(new PopStateEvent("popstate"));
}

const THEME_KEY = "katanoid_theme";
const ENV = import.meta.env.VITE_ENVIRONMENT || (import.meta.env.DEV ? "local" : "production");
const ENV_LABEL = ENV === "beta" ? "Beta" : ENV === "local" ? "Local" : null;

@customElement("katanoid-consumer")
export class KatanoidConsumer extends LitElement {
  protected override createRenderRoot(): HTMLElement {
    return this;
  }

  // ── Auth state ──
  @state() private user: UserProfile | null = null;
  @state() private authLoading = true;

  // ── Navigation ──
  @state() private currentView: ConsumerView = "chat";

  // ── Onboarding ──
  @state() private onboardingStep: OnboardingStep | null = null;
  @state() private onboardingComplete = false;

  // ── Credits ──
  @state() private credits: CreditBalance | null = null;

  // ── Subscription & Billing ──
  @state() private subscription: SubscriptionInfo | null = null;
  @state() private billingModalMode: "upgrade" | "buy-credits" | null = null;

  // ── BYOK ──
  @state() private apiKeyHint: string | null = null;
  @state() private openaiKeyHint: string | null = null;
  @state() private modelBrowserOpen = false;
  @state() private customModelsList: import("../models.ts").ModelEntry[] = [];

  // ── Gateway ──
  @state() private gatewayState: GatewayConnectionState = "disconnected";
  @state() private messages: ChatMessage[] = [];
  @state() private isAssistantTyping = false;

  // ── Streaming ──
  @state() private streamingMessageId: string | null = null;
  @state() private streamingContent = "";

  // ── Tool activity ──
  @state() private activeTools: string[] = [];
  @state() private toolStatus: "idle" | "running" | "done" = "idle";

  // ── Active session ──
  @state() private currentSessionId: string | null = null;

  // ── Sub-agents ──
  @state() private subAgentRuns: SubAgentRun[] = [];

  // ── Canvas ──
  @state() private canvasVisible = false;

  // ── Conversation mode ──
  @state() private conversationMode: ConversationMode = "normal";

  // ── Skill commands ──
  @state() private skillCommands: SkillSlashCommand[] = [];

  // ── Theme ──
  @state() private themeMode: ThemeMode = "light";

  // ── Mobile "More" sheet ──
  @state() private moreSheetOpen = false;

  private services: Services | null = null;
  private cleanupFns: Array<() => void> = [];
  private subAgentCleanup: (() => void) | null = null;

  override connectedCallback(): void {
    super.connectedCallback();
    this.initTheme();
    this.bootstrap();
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    for (const fn of this.cleanupFns) fn();
    this.cleanupFns = [];
    this.services?.gateway.disconnect();
    window.removeEventListener("hashchange", this.handleHashChange);
    window.removeEventListener("popstate", this.handlePopState);
  }

  // ── Theme Management ──

  private initTheme(): void {
    const stored = localStorage.getItem(THEME_KEY) as ThemeMode | null;
    this.themeMode = stored ?? "light";
    this.applyTheme();
  }

  private applyTheme(): void {
    const html = document.documentElement;
    if (this.themeMode === "system") {
      const prefersDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
      html.setAttribute("data-theme", prefersDark ? "dark" : "light");
    } else {
      html.setAttribute("data-theme", this.themeMode);
    }
  }

  setTheme(mode: ThemeMode): void {
    this.themeMode = mode;
    localStorage.setItem(THEME_KEY, mode);
    this.applyTheme();
  }

  // ── Bootstrap ──

  private async bootstrap(): Promise<void> {
    this.authLoading = true;

    this.services = await initServices();

    // Set up navigation
    this.currentView = getViewFromHash();
    this.onboardingStep = getOnboardingFromPath();
    window.addEventListener("hashchange", this.handleHashChange);
    window.addEventListener("popstate", this.handlePopState);

    // Set up gateway listeners
    this.cleanupFns.push(
      this.services.gateway.onStateChange((state) => {
        this.gatewayState = state;
      })
    );

    this.cleanupFns.push(
      this.services.gateway.onMessage((msg) => {
        this.messages = [...this.messages, msg];
        this.isAssistantTyping = false;
        this.streamingMessageId = null;
        this.streamingContent = "";
        this.activeTools = [];
        this.toolStatus = "idle";

        // Only client-side persist if gateway doesn't handle it server-side
        if (!this.services!.gateway.persistsMessages && this.currentSessionId) {
          this.services!.sessions.addMessage(this.currentSessionId, msg);
        }
      })
    );

    // Wire up streaming deltas for progressive rendering (real gateway only)
    if (this.services.gateway.onStreamDelta) {
      this.cleanupFns.push(
        this.services.gateway.onStreamDelta((msgId, content) => {
          this.streamingMessageId = msgId;
          this.streamingContent = content;
        })
      );
    }

    // Wire up tool activity events (real gateway only)
    if (this.services.gateway.onToolActivity) {
      this.cleanupFns.push(
        this.services.gateway.onToolActivity((event) => {
          if (event.type === "tool_start") {
            this.activeTools = event.tools ?? [];
            this.toolStatus = "running";
          } else if (event.type === "tool_result") {
            // Tool result received — will continue streaming shortly
            this.toolStatus = "done";
          }
        })
      );
    }

    // Wire up sub-agent spawned events from SSE stream
    if (this.services.gateway.onSubAgentEvent) {
      this.cleanupFns.push(
        this.services.gateway.onSubAgentEvent((event) => {
          if (event.type === "sub_agent_spawned" && event.runId) {
            // Add a placeholder run; Realtime subscription will update it
            const placeholder: SubAgentRun = {
              id: event.runId,
              parentSessionId: this.currentSessionId ?? "",
              task: event.task ?? "",
              label: event.label ?? "Task",
              status: "pending",
              resultSummary: null,
              resultDetail: null,
              error: null,
              creditsUsed: 0,
              startedAt: null,
              completedAt: null,
              createdAt: new Date().toISOString(),
            };
            this.subAgentRuns = [...this.subAgentRuns, placeholder];
          }
        })
      );
    }

    // Wire up canvas events from SSE stream
    if (this.services.gateway.onCanvasEvent) {
      this.cleanupFns.push(
        this.services.gateway.onCanvasEvent((event: CanvasEvent) => {
          this.handleCanvasEvent(event);
        })
      );
    }

    // Set up credit listeners
    this.cleanupFns.push(
      this.services.credits.onBalanceChange((balance) => {
        this.credits = balance;
      })
    );

    // Use onAuthStateChange as the SOLE mechanism for session detection.
    // Supabase fires an INITIAL_SESSION event which gives us the current
    // session. We wrap the first callback in a promise so bootstrap can
    // wait for it (including loadUserData) before clearing the spinner.
    //
    // Fallback: if the event doesn't fire within 5s (corrupted storage),
    // we call getSession() directly.
    const initialReady = new Promise<void>((resolve) => {
      let handled = false;

      // Timeout fallback
      const timer = setTimeout(async () => {
        if (handled) return;
        handled = true;
        console.warn("[App] onAuthStateChange did not fire, falling back to getSession");
        try {
          const user = await this.services!.auth.getCurrentUser();
          await this.handleAuthUser(user);
        } catch (err) {
          console.error("[App] Fallback getCurrentUser failed:", err);
          this.onboardingStep = null;
        }
        resolve();
      }, 5000);

      this.cleanupFns.push(
        this.services!.auth.onAuthChange(async (user) => {
          if (!handled) {
            // First callback = initial session state
            handled = true;
            clearTimeout(timer);
            await this.handleAuthUser(user);
            resolve();
            return;
          }
          // Subsequent callbacks = sign-in, sign-out, token refresh
          await this.handleAuthUser(user);
        })
      );
    });

    await initialReady;
    this.authLoading = false;
  }

  private handleHashChange = (): void => {
    this.currentView = getViewFromHash();
  };

  private handlePopState = (): void => {
    this.onboardingStep = getOnboardingFromPath();
  };

  private async handleAuthUser(user: UserProfile | null): Promise<void> {
    if (user) {
      this.user = user;

      // Hydrate localStorage model prefs from server profile
      if (user.modelPreferences) {
        saveTierPreferences(user.modelPreferences as TierPreferences);
      }

      if (!user.onboardingComplete) {
        // Auto-complete onboarding with defaults — no wizard steps
        await this.services!.auth.updateProfile({
          assistantName: "Katanoid",
          onboardingComplete: true,
        });
        this.user = { ...this.user!, assistantName: "Katanoid", onboardingComplete: true };
      }
      this.onboardingComplete = true;
      this.onboardingStep = null;
      if (window.location.pathname !== "/") {
        window.history.replaceState(null, "", "/");
      }
      await this.loadUserData();
    } else {
      this.user = null;
      this.credits = null;
      this.subscription = null;
      this.billingModalMode = null;
      this.apiKeyHint = null;
      this.openaiKeyHint = null;
      this.modelBrowserOpen = false;
      this.customModelsList = [];
      clearCustomModels();
      this.skillCommands = [];
      this.messages = [];
      this.currentSessionId = null;
      this.subAgentRuns = [];
      this.subAgentCleanup?.();
      this.subAgentCleanup = null;
      this.onboardingStep = getOnboardingFromPath();
      this.onboardingComplete = false;
    }
  }

  private async loadUserData(): Promise<void> {
    if (!this.services) return;

    try {
      this.credits = await this.services.credits.getBalance();
    } catch (err) {
      console.warn("[App] Failed to load credits:", err);
    }

    try {
      this.subscription = await this.services.billing.getSubscription();
      this.apiKeyHint = this.subscription.apiKeyHint ?? null;
      this.openaiKeyHint = this.subscription.openaiKeyHint ?? null;
    } catch (err) {
      console.warn("[App] Failed to load subscription:", err);
    }

    // Load custom models for BYOK users
    try {
      await this.loadCustomModels();
    } catch (err) {
      console.warn("[App] Failed to load custom models:", err);
    }

    // Load skill slash commands for installed skills
    try {
      await this.loadSkillCommands();
    } catch (err) {
      console.warn("[App] Failed to load skill commands:", err);
    }

    try {
      await this.services.gateway.connect();
    } catch (err) {
      console.warn("[App] Failed to connect gateway:", err);
    }

    // Handle checkout return from Stripe
    this.handleCheckoutReturn();
  }

  // ── Event Handlers ──

  private navigateTo(view: ConsumerView): void {
    this.currentView = view;
    setViewHash(view);
  }

  private async handleSendMessage(e: CustomEvent<{ content: string; imageDataUri?: string }>): Promise<void> {
    const content = e.detail.content;
    const imageDataUri = e.detail.imageDataUri;
    if (!content.trim() || !this.services) return;

    // Auto-create a session on first message if none active
    if (!this.currentSessionId) {
      try {
        // Use first ~40 chars of the message as session title
        const title = content.length > 40 ? content.slice(0, 40).trim() + "\u2026" : content;
        const session = await this.services.sessions.createSession(title);
        this.currentSessionId = session.id;
        // Subscribe to sub-agent updates for the new session
        this.subscribeSubAgents(session.id);
      } catch {
        // Non-blocking — continue without persistence
        console.warn("[App] Failed to create session, messages won't persist");
      }
    }

    const userMsg: ChatMessage = {
      id: `user-${Date.now()}`,
      role: "user",
      content,
      timestamp: Date.now(),
    };

    this.messages = [...this.messages, userMsg];
    this.isAssistantTyping = true;

    // Only client-side persist if gateway doesn't handle it server-side
    if (!this.services.gateway.persistsMessages && this.currentSessionId) {
      this.services.sessions.addMessage(this.currentSessionId, userMsg);
    }

    try {
      const toolPrefs = loadToolPreferences();
      await this.services.gateway.sendMessage(content, {
        sessionId: this.currentSessionId ?? undefined,
        mode: this.conversationMode,
        model: getModelForTier(this.conversationMode),
        imageDataUri,
        visionModel: toolPrefs.visionModel,
        ttsVoice: toolPrefs.ttsVoice,
      });
    } catch (err) {
      console.error("[App] sendMessage failed:", err);
      this.isAssistantTyping = false;

      // Show error to user as an assistant message
      const errorMsg: ChatMessage = {
        id: `error-${Date.now()}`,
        role: "assistant",
        content: `Something went wrong sending your message. ${err instanceof Error ? err.message : "Please try again."}`,
        timestamp: Date.now(),
      };
      this.messages = [...this.messages, errorMsg];
    }
  }

  private handleSignOut = async (): Promise<void> => {
    this.services?.gateway.disconnect();
    await this.services?.auth.signOut();
  };

  private handleThemeChange = (e: CustomEvent<{ theme: ThemeMode }>): void => {
    this.setTheme(e.detail.theme);
  };

  private handleModelPrefsChange = (e: CustomEvent<{ prefs: TierPreferences }>): void => {
    // localStorage is already updated by the settings view.
    // Persist to the server profile so it syncs across devices.
    if (this.services && this.user) {
      this.services.auth.updateProfile({
        modelPreferences: e.detail.prefs as Record<string, string>,
      });
    }
  };

  private handleToolPrefsChange = (_e: CustomEvent<{ prefs: ToolPreferences }>): void => {
    // localStorage is already updated by the settings view.
    // Tool preferences are sent with each message, no server-side persistence needed.
  };

  private handleAssistantSettingsChange = (
    e: CustomEvent<{ assistantName: string; customInstructions: string | null }>
  ): void => {
    if (this.services && this.user) {
      this.services.auth.updateProfile({
        assistantName: e.detail.assistantName,
        customInstructions: e.detail.customInstructions,
      });
      // Update local state so sidebar brand name reflects change immediately
      this.user = {
        ...this.user,
        assistantName: e.detail.assistantName,
        customInstructions: e.detail.customInstructions,
      };
    }
  };

  private handleMemoryToggle = (
    e: CustomEvent<{ enabled: boolean }>
  ): void => {
    if (this.services && this.user) {
      this.services.auth.updateProfile({
        memoryEnabled: e.detail.enabled,
      });
      this.user = {
        ...this.user,
        memoryEnabled: e.detail.enabled,
      };
    }
  };

  private handleChangePassword = async (
    e: CustomEvent<{ currentPassword: string; newPassword: string }>
  ): Promise<void> => {
    if (!this.services) return;
    try {
      const result = await this.services.auth.changePassword(
        e.detail.currentPassword,
        e.detail.newPassword
      );
      const settingsEl = this.querySelector("katanoid-settings-view") as
        import("../views/settings.ts").KatanoidSettingsView | null;
      if (result.error) {
        settingsEl?.passwordChangeFailed(result.error);
      } else {
        settingsEl?.passwordChanged();
      }
    } catch (err) {
      const settingsEl = this.querySelector("katanoid-settings-view") as
        import("../views/settings.ts").KatanoidSettingsView | null;
      settingsEl?.passwordChangeFailed(
        err instanceof Error ? err.message : "Failed to change password"
      );
    }
  };

  // ── BYOK Handlers ──

  private isDevMode = import.meta.env.VITE_DEV_MODE === "true";

  private async loadCustomModels(): Promise<void> {
    // In dev mode, custom models are managed purely in-memory
    if (this.isDevMode) {
      this.customModelsList = [...getCustomModels()];
      return;
    }

    const userId = getSessionDirect()?.userId;
    if (!userId) return;
    try {
      const resp = await directRestRequest(
        `/user_custom_models?user_id=eq.${userId}&select=model_id,display_name,tier,credit_cost,context_length`
      );
      if (resp.ok) {
        const rows = await resp.json();
        const models: ModelEntry[] = (rows as Array<{
          model_id: string;
          display_name: string;
          tier: string;
          credit_cost: number;
        }>).map((r) => ({
          id: r.model_id,
          label: r.display_name || r.model_id,
          provider: r.model_id.split("/")[0] ?? "OpenRouter",
          tier: r.tier as ModelEntry["tier"],
          creditCost: r.credit_cost,
        }));
        setCustomModels(models);
        this.customModelsList = models;
      }
    } catch (err) {
      console.warn("[App] Failed to load custom models:", err);
    }
  }

  private async loadSkillCommands(): Promise<void> {
    if (this.isDevMode) {
      // Dev mode: provide sample slash commands so the command picker can be tested
      this.skillCommands = [
        { skillName: "Weather", name: "weather", description: "Check the weather for a location", prepend_prompt: "Check the current weather for: " },
        { skillName: "Email Drafter", name: "email", description: "Draft a professional email", prepend_prompt: "Help me draft an email: " },
        { skillName: "GitHub Summary", name: "github", description: "Summarize a GitHub PR, issue, or repo", prepend_prompt: "Summarize this GitHub page: " },
        { skillName: "Coding Tutor", name: "teach", description: "Learn about a programming concept", prepend_prompt: "Teach me about: " },
        { skillName: "Translator", name: "translate", description: "Translate text to another language", prepend_prompt: "Translate the following: " },
        { skillName: "Daily Digest", name: "digest", description: "Get your personalized daily news briefing", prepend_prompt: "Give me my daily news digest. " },
        { skillName: "Writing Coach", name: "review", description: "Get feedback on your writing", prepend_prompt: "Review and improve this writing: " },
        { skillName: "Recipe Finder", name: "recipe", description: "Find a recipe based on ingredients or cuisine", prepend_prompt: "Find me a recipe for: " },
        { skillName: "Meeting Notes", name: "notes", description: "Structure meeting notes with action items", prepend_prompt: "Organize these meeting notes into a structured summary: " },
        { skillName: "Hacker News", name: "hn", description: "Browse top Hacker News stories", prepend_prompt: "Show me the top stories from Hacker News right now. " },
      ];
      return;
    }

    const userId = getSessionDirect()?.userId;
    if (!userId) return;

    try {
      // Fetch enabled user skills with their skill data (including commands)
      const resp = await directRestRequest(
        `/user_skills?user_id=eq.${userId}&enabled=eq.true&select=skill_id,skills(name,emoji,commands)`
      );
      if (!resp.ok) return;

      const rows = await resp.json() as Array<{
        skill_id: string;
        skills: { name: string; emoji: string | null; commands: Array<{ name: string; description: string; prepend_prompt: string }> | null } | null;
      }>;

      const commands: SkillSlashCommand[] = [];
      for (const row of rows) {
        if (!row.skills?.commands) continue;
        for (const cmd of row.skills.commands) {
          commands.push({
            skillName: row.skills.name,
            skillEmoji: row.skills.emoji ?? undefined,
            name: cmd.name,
            description: cmd.description,
            prepend_prompt: cmd.prepend_prompt,
          });
        }
      }
      this.skillCommands = commands;
    } catch (err) {
      console.warn("[App] Failed to load skill commands:", err);
    }
  }

  private handleApiKeySave = async (e: CustomEvent<{ key: string }>): Promise<void> => {
    if (!this.services) return;
    try {
      // Validate first
      const validation = await this.services.billing.validateApiKey(e.detail.key);
      if (!validation.valid) {
        const settingsEl = this.querySelector("katanoid-settings-view") as
          import("../views/settings.ts").KatanoidSettingsView | null;
        settingsEl?.apiKeyFailed(validation.error ?? "Invalid API key.");
        return;
      }
      // Save
      const { hint } = await this.services.billing.saveApiKey(e.detail.key);
      this.apiKeyHint = hint;
      const settingsEl = this.querySelector("katanoid-settings-view") as
        import("../views/settings.ts").KatanoidSettingsView | null;
      settingsEl?.apiKeySaved(hint);
    } catch (err) {
      const settingsEl = this.querySelector("katanoid-settings-view") as
        import("../views/settings.ts").KatanoidSettingsView | null;
      settingsEl?.apiKeyFailed(err instanceof Error ? err.message : "Failed to save key.");
    }
  };

  private handleApiKeyDelete = async (): Promise<void> => {
    if (!this.services) return;
    try {
      await this.services.billing.deleteApiKey();
      this.apiKeyHint = null;
      const settingsEl = this.querySelector("katanoid-settings-view") as
        import("../views/settings.ts").KatanoidSettingsView | null;
      settingsEl?.apiKeyDeleted();
    } catch (err) {
      console.error("[App] Failed to delete API key:", err);
    }
  };

  // ── OpenAI Key Handlers ──

  private handleOpenaiKeySave = async (e: CustomEvent<{ key: string }>): Promise<void> => {
    if (!this.services) return;
    try {
      const validation = await this.services.billing.validateOpenaiKey(e.detail.key);
      if (!validation.valid) {
        const settingsEl = this.querySelector("katanoid-settings-view") as
          import("../views/settings.ts").KatanoidSettingsView | null;
        settingsEl?.openaiKeyFailed(validation.error ?? "Invalid OpenAI key.");
        return;
      }
      const { hint } = await this.services.billing.saveOpenaiKey(e.detail.key);
      this.openaiKeyHint = hint;
      const settingsEl = this.querySelector("katanoid-settings-view") as
        import("../views/settings.ts").KatanoidSettingsView | null;
      settingsEl?.openaiKeySaved(hint);
    } catch (err) {
      const settingsEl = this.querySelector("katanoid-settings-view") as
        import("../views/settings.ts").KatanoidSettingsView | null;
      settingsEl?.openaiKeyFailed(err instanceof Error ? err.message : "Failed to save key.");
    }
  };

  private handleOpenaiKeyDelete = async (): Promise<void> => {
    if (!this.services) return;
    try {
      await this.services.billing.deleteOpenaiKey();
      this.openaiKeyHint = null;
      const settingsEl = this.querySelector("katanoid-settings-view") as
        import("../views/settings.ts").KatanoidSettingsView | null;
      settingsEl?.openaiKeyDeleted();
    } catch (err) {
      console.error("[App] Failed to delete OpenAI key:", err);
    }
  };

  private handleBrowseModels = (): void => {
    this.modelBrowserOpen = true;
  };

  private handleModelBrowserClose = (): void => {
    this.modelBrowserOpen = false;
  };

  private handleModelAdd = async (
    e: CustomEvent<{ model: ModelEntry; contextLength: number | null }>
  ): Promise<void> => {
    const { model, contextLength } = e.detail;

    // Update in-memory list immediately (works for both mock and real)
    const current = getCustomModels();
    if (!current.find((m) => m.id === model.id)) {
      const updated = [...current, model];
      setCustomModels(updated);
      this.customModelsList = updated;
    }

    // Persist to DB in real mode
    if (!this.isDevMode) {
      const userId = getSessionDirect()?.userId;
      if (!userId) return;
      try {
        await directRestRequest("/user_custom_models", {
          method: "POST",
          headers: { "Content-Type": "application/json", Prefer: "return=minimal" },
          body: JSON.stringify({
            user_id: userId,
            model_id: model.id,
            display_name: model.label,
            tier: model.tier,
            credit_cost: model.creditCost,
            context_length: contextLength,
          }),
        });
      } catch (err) {
        console.warn("[App] Failed to persist custom model:", err);
      }
    }
  };

  private handleModelRemove = async (
    e: CustomEvent<{ modelId: string }>
  ): Promise<void> => {
    // Update in-memory list immediately
    const updated = getCustomModels().filter((m) => m.id !== e.detail.modelId);
    setCustomModels(updated);
    this.customModelsList = updated;

    // Persist to DB in real mode
    if (!this.isDevMode) {
      const userId = getSessionDirect()?.userId;
      if (!userId) return;
      try {
        await directRestRequest(
          `/user_custom_models?user_id=eq.${userId}&model_id=eq.${e.detail.modelId}`,
          { method: "DELETE" }
        );
      } catch (err) {
        console.warn("[App] Failed to delete custom model from DB:", err);
      }
    }
  };

  private handleBillingAction = async (
    e: CustomEvent<{ action: "upgrade" | "buy-credits" | "manage" }>
  ): Promise<void> => {
    const { action } = e.detail;

    if (action === "manage") {
      // Redirect to Stripe Customer Portal
      if (!this.services) return;
      try {
        const { url } = await this.services.billing.createPortalSession();
        window.location.href = url;
      } catch (err) {
        console.error("[App] Failed to create portal session:", err);
      }
      return;
    }

    // Open billing modal for upgrade or buy-credits
    this.billingModalMode = action;
  };

  private handleBillingCheckout = async (
    e: CustomEvent<{
      type: "subscription" | "credit-pack";
      plan?: "pro" | "pro_plus" | "byok";
      packSize?: "small" | "medium" | "large";
    }>
  ): Promise<void> => {
    if (!this.services) return;

    const { type, plan } = e.detail;
    const currentPlan = this.subscription?.plan ?? "free";

    // If user already has a paid subscription and is switching plans, use update instead of checkout
    if (type === "subscription" && plan && currentPlan !== "free") {
      try {
        await this.services.billing.updateSubscription({ plan });
        // Refresh billing data immediately
        this.subscription = await this.services.billing.getSubscription();
        this.credits = await this.services.credits.getBalance();
        this.billingModalMode = null;
      } catch (err) {
        console.error("[App] Failed to update subscription:", err);
        this.billingModalMode = null;
      }
      return;
    }

    try {
      const { url } = await this.services.billing.createCheckoutSession(e.detail);

      // Mock billing returns hash URLs — stay on the page and refresh state
      if (url.startsWith("#")) {
        this.subscription = await this.services.billing.getSubscription();
        this.apiKeyHint = this.subscription.apiKeyHint ?? null;
        this.openaiKeyHint = this.subscription.openaiKeyHint ?? null;
        this.credits = await this.services.credits.getBalance();
        this.billingModalMode = null;
        return;
      }

      window.location.href = url;
    } catch (err) {
      console.error("[App] Failed to create checkout session:", err);
      this.billingModalMode = null;
    }
  };

  private handleCancelPendingDowngrade = async (): Promise<void> => {
    if (!this.services) return;
    try {
      await this.services.billing.cancelPendingDowngrade();
      // Refresh subscription to clear pendingPlan in UI
      this.subscription = await this.services.billing.getSubscription();
      this.billingModalMode = null;
    } catch (err) {
      console.error("[App] Failed to cancel pending downgrade:", err);
    }
  };

  private handleBillingClose = (): void => {
    this.billingModalMode = null;
  };

  private handleCheckoutReturn(): void {
    const params = new URLSearchParams(window.location.search);
    const checkout = params.get("checkout");
    if (!checkout) return;

    // Clean up the URL
    const url = new URL(window.location.href);
    url.searchParams.delete("checkout");
    window.history.replaceState({}, "", url.toString());

    if (checkout === "success") {
      console.log("[App] Checkout successful — polling for updated billing data");
      const originalBonus = this.credits?.bonusCredits ?? 0;
      const originalPlan = this.subscription?.plan ?? "free";
      let attempts = 0;

      const poll = async (): Promise<void> => {
        if (!this.services || attempts >= 5) return;
        attempts++;
        try {
          this.credits = await this.services.credits.getBalance();
          this.subscription = await this.services.billing.getSubscription();

          const newBonus = this.credits?.bonusCredits ?? 0;
          const newPlan = this.subscription?.plan ?? "free";
          if (newBonus !== originalBonus || newPlan !== originalPlan) {
            console.log("[App] Billing data updated after checkout");
            return;
          }
        } catch (err) {
          console.warn("[App] Poll attempt failed:", err);
        }
        setTimeout(poll, 2000);
      };

      // Give webhook a moment to process, then start polling
      setTimeout(poll, 2000);
    }
  }

  private handleNewSession = (): void => {
    // Clear active session — a new one will be auto-created on first message
    this.currentSessionId = null;
    this.messages = [];
    this.streamingMessageId = null;
    this.streamingContent = "";
    this.activeTools = [];
    this.toolStatus = "idle";
    this.subAgentRuns = [];
    this.subAgentCleanup?.();
    this.subAgentCleanup = null;
    this.forkMode = false;
    this.canvasVisible = false;
    this.navigateTo("chat");
  };

  private handleOpenSession = async (e: CustomEvent<{ sessionId: string }>): Promise<void> => {
    if (!this.services) return;

    const sessionId = e.detail.sessionId;
    this.currentSessionId = sessionId;
    this.messages = []; // Clear while loading
    this.navigateTo("chat");

    // Clean up previous sub-agent subscription
    this.subAgentCleanup?.();
    this.subAgentCleanup = null;
    this.subAgentRuns = [];

    try {
      let messages = await this.services.sessions.getMessages(sessionId);

      // Resolve signed URLs for any TTS audio in historical messages
      if (this.services.sessions.resolveAudioUrls) {
        messages = await this.services.sessions.resolveAudioUrls(messages);
      }

      // Resolve signed URLs for any screenshots in historical messages
      if (this.services.sessions.resolveScreenshotUrls) {
        messages = await this.services.sessions.resolveScreenshotUrls(messages);
      }

      this.messages = messages;
    } catch {
      console.warn("[App] Failed to load session messages");
    }

    // Load existing sub-agent runs and subscribe to updates
    this.subscribeSubAgents(sessionId);
  };

  private isPaidPlan(): boolean {
    const plan = this.subscription?.plan ?? "free";
    return plan === "pro" || plan === "pro_plus" || plan === "byok";
  }

  /**
   * Parse selected text into a structured memory fact.
   * Infers a snake_case key from the first meaningful words
   * and attempts to categorise based on keyword matching.
   */
  private parseMemoryFact(text: string): { category: string; factKey: string; factValue: string } {
    // ── Category inference ──
    const lower = text.toLowerCase();
    const categoryKeywords: Record<string, string[]> = {
      personal: ["name", "birthday", "born", "age", "live", "lives", "from", "hometown", "family", "married", "kids", "children", "pet", "dog", "cat"],
      preference: ["like", "love", "prefer", "favorite", "favourite", "enjoy", "hate", "dislike", "rather", "want"],
      work: ["job", "work", "company", "role", "position", "title", "career", "employer", "business", "office", "manager", "team"],
      technical: ["code", "coding", "programming", "language", "framework", "stack", "python", "typescript", "javascript", "react", "node", "rust", "go", "api", "database", "linux", "mac", "windows", "git", "docker", "aws", "gcp"],
      relationship: ["friend", "wife", "husband", "partner", "brother", "sister", "mother", "father", "colleague", "boss", "mentor"],
    };

    let category = "other";
    let bestScore = 0;
    for (const [cat, keywords] of Object.entries(categoryKeywords)) {
      const score = keywords.filter((kw) => lower.includes(kw)).length;
      if (score > bestScore) {
        bestScore = score;
        category = cat;
      }
    }

    // ── Key generation ──
    // Take the first few meaningful words, strip filler, convert to snake_case
    const stopWords = new Set(["i", "my", "the", "a", "an", "is", "am", "are", "was", "were", "be", "been", "have", "has", "had", "do", "does", "did", "will", "would", "could", "should", "can", "may", "that", "this", "it", "its", "of", "in", "on", "at", "to", "for", "with", "and", "or", "but", "so", "if", "not", "no", "very", "really", "just", "also"]);
    const words = text
      .replace(/[^a-zA-Z0-9\s]/g, "")
      .split(/\s+/)
      .map((w) => w.toLowerCase())
      .filter((w) => w.length > 1 && !stopWords.has(w));

    const keyWords = words.slice(0, 4);
    const factKey = keyWords.length > 0
      ? keyWords.join("_")
      : `manual_${Date.now()}`;

    return { category, factKey, factValue: text };
  }

  private handleSaveToMemory = async (e: CustomEvent<{ text: string }>): Promise<void> => {
    if (!this.user) return;

    const text = e.detail.text.trim();
    if (!text) return;

    const { category, factKey, factValue } = this.parseMemoryFact(text);

    try {
      const resp = await directRestRequest("/memory_facts", {
        method: "POST",
        body: JSON.stringify({
          user_id: this.user.id,
          category,
          fact_key: factKey,
          fact_value: factValue,
          confidence: 1.0,
          source: "manual",
        }),
        headers: { Prefer: "return=minimal" },
      });

      const chatView = this.querySelector("katanoid-chat-view") as HTMLElement & { memorySaved(): void; memorySaveFailed(err: string): void } | null;
      if (resp.ok) {
        chatView?.memorySaved();
      } else {
        const err = await resp.text().catch(() => "Unknown error");
        chatView?.memorySaveFailed(err);
      }
    } catch (err) {
      const chatView = this.querySelector("katanoid-chat-view") as HTMLElement & { memorySaveFailed(err: string): void } | null;
      chatView?.memorySaveFailed(err instanceof Error ? err.message : String(err));
    }
  };

  // ── Fork Mode ──
  @state() private forkMode = false;

  // ── Sub-Agent Management ──

  private async subscribeSubAgents(sessionId: string): Promise<void> {
    if (!this.services) return;

    try {
      const runs = await this.services.subAgents.listRuns(sessionId);
      this.subAgentRuns = runs;
    } catch (err) {
      console.warn("[App] Failed to load sub-agent runs:", err);
    }

    // Subscribe to Realtime updates
    this.subAgentCleanup = this.services.subAgents.onRunUpdate(
      sessionId,
      (updatedRun) => {
        const idx = this.subAgentRuns.findIndex((r) => r.id === updatedRun.id);
        if (idx >= 0) {
          const updated = [...this.subAgentRuns];
          updated[idx] = updatedRun;
          this.subAgentRuns = updated;
        } else {
          this.subAgentRuns = [...this.subAgentRuns, updatedRun];
        }
      }
    );
  }

  private handleForkSession = async (
    e: CustomEvent<{ messageId: string }>
  ): Promise<void> => {
    if (!this.services || !this.currentSessionId) return;
    if (!this.services.sessions.forkSession) return;

    try {
      const forked = await this.services.sessions.forkSession(
        this.currentSessionId,
        e.detail.messageId
      );
      // Navigate to the forked session
      this.forkMode = false;
      this.currentSessionId = forked.id;
      this.messages = [];
      this.streamingMessageId = null;
      this.streamingContent = "";
      this.subAgentRuns = [];
      this.subAgentCleanup?.();
      this.subAgentCleanup = null;

      // Load messages for the forked session
      try {
        this.messages = await this.services.sessions.getMessages(forked.id);
      } catch {
        console.warn("[App] Failed to load forked session messages");
      }
      this.subscribeSubAgents(forked.id);
    } catch (err) {
      console.error("[App] Failed to fork session:", err);
    }
  };

  private handleOpenSessionFork = async (
    e: CustomEvent<{ sessionId: string }>
  ): Promise<void> => {
    // Open the session in fork mode
    await this.handleOpenSession(e);
    this.forkMode = true;
  };

  private handleEnterForkMode = (): void => {
    this.forkMode = true;
  };

  private handleExitForkMode = (): void => {
    this.forkMode = false;
  };

  private handleCancelSubAgent = async (
    e: CustomEvent<{ runId: string }>
  ): Promise<void> => {
    if (!this.services) return;
    try {
      await this.services.subAgents.cancelRun(e.detail.runId);
      // Optimistically update local state
      this.subAgentRuns = this.subAgentRuns.map((r) =>
        r.id === e.detail.runId ? { ...r, status: "cancelled" as const } : r
      );
    } catch (err) {
      console.warn("[App] Failed to cancel sub-agent:", err);
    }
  };

  // ── Canvas ──

  private handleCanvasEvent(event: CanvasEvent): void {
    console.log("[Canvas] handleCanvasEvent:", event.type, { hasJsonl: !!event.jsonl, hasUrl: !!event.url, hasJs: !!event.javaScript });

    switch (event.type) {
      case "canvas_present":
      case "canvas_a2ui_push":
      case "canvas_navigate":
        this.canvasVisible = true;
        // Wait for Lit to render the canvas panel, then dispatch content
        this.updateComplete.then(() => {
          // Chat view also needs to update (it conditionally renders the panel)
          const chatView = this.querySelector("katanoid-chat") as LitElement | null;
          const waitForChat = chatView ? chatView.updateComplete : Promise.resolve(true);
          return waitForChat.then(() => {});
        }).then(() => {
          const p = this.querySelector("katanoid-canvas-panel") as KatanoidCanvasPanel | null;
          console.log("[Canvas] Panel found after updateComplete:", !!p);
          if (!p) return;

          if (event.type === "canvas_navigate" || (event.type === "canvas_present" && event.url)) {
            if (event.url) p.navigate(event.url);
          } else if (event.jsonl) {
            console.log("[Canvas] Calling pushA2UI, jsonl length:", event.jsonl.length);
            p.pushA2UI(event.jsonl);
          }
        });
        break;

      case "canvas_hide":
        this.canvasVisible = false;
        break;

      case "canvas_eval": {
        const panel = this.querySelector("katanoid-canvas-panel") as KatanoidCanvasPanel | null;
        if (panel && event.javaScript) {
          panel.evalJS(event.javaScript);
        }
        break;
      }

      case "canvas_a2ui_reset": {
        const panel = this.querySelector("katanoid-canvas-panel") as KatanoidCanvasPanel | null;
        if (panel) panel.reset();
        break;
      }
    }
  }

  private handleCanvasClose = (): void => {
    this.canvasVisible = false;
  };

  // ── Render ──

  protected override render(): TemplateResult {
    // Download page is accessible without authentication
    if (this.currentView === "download") {
      return html`<katanoid-download-view></katanoid-download-view>`;
    }

    if (this.authLoading) {
      return html`
        <div class="app-shell" style="align-items: center; justify-content: center;">
          <div class="spinner spinner--lg"></div>
        </div>
      `;
    }

    if (!this.user) {
      if (this.onboardingStep === "terms" || this.onboardingStep === "privacy") {
        return html`
          <katanoid-terms
            .page=${this.onboardingStep}
            @back=${() => { window.history.back(); }}
          ></katanoid-terms>
        `;
      }
      if (this.onboardingStep === "signup" || this.onboardingStep === "login") {
        return html`
          <katanoid-onboarding
            .authMode=${this.onboardingStep === "login" ? "signin" : "signup"}
            @back-to-landing=${() => { navigateTo("/"); }}
          ></katanoid-onboarding>
        `;
      }
      return html`
        <katanoid-landing
          @get-started=${() => { navigateTo("/signup"); }}
          @sign-in=${() => { navigateTo("/login"); }}
        ></katanoid-landing>
      `;
    }

    const assistantName = this.user.assistantName || "Katanoid";

    return html`
      <div class="app-shell">
        ${this.renderSidebar(assistantName)}
        <div class="app-main-column">
          ${ENV_LABEL ? html`<div class="beta-banner">You are using the ${ENV_LABEL.toLowerCase()} version of Katanoid</div>` : nothing}
          <div class="main-content">
            ${this.renderCurrentView(assistantName)}
          </div>
        </div>
        ${this.renderMobileTabBar()}
        ${this.moreSheetOpen ? this.renderMoreSheet() : nothing}
        ${this.billingModalMode
          ? html`
              <katanoid-billing-modal
                .mode=${this.billingModalMode}
                .currentPlan=${this.subscription?.plan ?? "free"}
                .pendingPlan=${this.subscription?.pendingPlan ?? null}
                .currentPeriodEnd=${this.subscription?.currentPeriodEnd ?? null}
                @billing-checkout=${this.handleBillingCheckout}
                @billing-cancel-downgrade=${this.handleCancelPendingDowngrade}
                @billing-close=${this.handleBillingClose}
              ></katanoid-billing-modal>
            `
          : nothing}
        ${this.modelBrowserOpen
          ? html`
              <katanoid-model-browser
                @model-add=${this.handleModelAdd}
                @model-remove=${this.handleModelRemove}
                @model-browser-close=${this.handleModelBrowserClose}
              ></katanoid-model-browser>
            `
          : nothing}
      </div>
    `;
  }

  private renderSidebar(assistantName: string): TemplateResult {
    const remaining = this.credits ? getRemaining(this.credits) : 0;
    const total = this.credits ? getTotal(this.credits) : 0;
    const pct = total > 0 ? Math.round((remaining / total) * 100) : 0;
    const level = this.credits ? getCreditLevel(this.credits) : "high";

    return html`
      <aside class="sidebar">
        <div class="sidebar-header">
          <div class="sidebar-logo">
            <svg viewBox="0 0 48 48" fill="none">
              <path d="M24 4L6 14v20l18 10 18-10V14L24 4z" opacity="0.9"/>
              <path d="M24 4L6 14l18 10 18-10L24 4z"/>
            </svg>
          </div>
          <span class="sidebar-brand">${assistantName}</span>
          ${ENV_LABEL ? html`<span class="beta-badge">${ENV_LABEL}</span>` : nothing}
        </div>

        <nav class="sidebar-nav">
          ${TABS.map(
            (tab) => html`
              <button
                class="sidebar-nav-item"
                aria-selected=${this.currentView === tab.id}
                @click=${() => this.navigateTo(tab.id)}
              >
                <span class="sidebar-nav-icon" .innerHTML=${tab.icon}></span>
                ${tab.label}
              </button>
            `
          )}
        </nav>

        <div class="sidebar-footer">
          ${this.credits
            ? html`
                <div class="sidebar-credits">
                  ${remaining} / ${total} credits
                  <div class="sidebar-credits-bar">
                    <div
                      class="sidebar-credits-fill ${level === "low" ? "sidebar-credits-fill--low" : level === "medium" ? "sidebar-credits-fill--low" : ""}"
                      style="width: ${pct}%"
                    ></div>
                  </div>
                </div>
              `
            : nothing}
          <div class="sidebar-email">${this.user?.email ?? ""}</div>
        </div>
      </aside>
    `;
  }

  private renderCurrentView(assistantName: string): TemplateResult {
    switch (this.currentView) {
      case "chat":
        return html`
          <katanoid-chat-view
            .messages=${this.messages}
            .isTyping=${this.isAssistantTyping}
            .gatewayState=${this.gatewayState}
            .assistantName=${assistantName}
            .streamingMessageId=${this.streamingMessageId}
            .streamingContent=${this.streamingContent}
            .conversationMode=${this.conversationMode}
            .activeTools=${this.activeTools}
            .toolStatus=${this.toolStatus}
            .memoryEnabled=${this.user?.memoryEnabled !== false && this.isPaidPlan()}
            .skillCommands=${this.skillCommands}
            .subAgentRuns=${this.subAgentRuns}
            .forkMode=${this.forkMode}
            .canvasVisible=${this.canvasVisible}
            @send-message=${this.handleSendMessage}
            @new-session=${this.handleNewSession}
            @save-to-memory=${this.handleSaveToMemory}
            @cancel-sub-agent=${this.handleCancelSubAgent}
            @fork-session=${this.handleForkSession}
            @exit-fork-mode=${this.handleExitForkMode}
            @canvas-close=${this.handleCanvasClose}
            @mode-change=${(e: CustomEvent<{ mode: ConversationMode }>) => {
              this.conversationMode = e.detail.mode;
            }}
          ></katanoid-chat-view>
        `;
      case "sessions":
        return html`
          <katanoid-sessions-view
            @new-session=${this.handleNewSession}
            @open-session=${this.handleOpenSession}
            @open-session-fork=${this.handleOpenSessionFork}
          ></katanoid-sessions-view>
        `;
      case "channels":
        return html`
          <katanoid-channels-view
            @channels-changed=${() => this.loadUserData()}
          ></katanoid-channels-view>
        `;
      case "usage":
        return html`
          <katanoid-usage-view
            .credits=${this.credits}
            @billing-action=${this.handleBillingAction}
          ></katanoid-usage-view>
        `;
      case "automations":
        return html`
          <katanoid-automations-view></katanoid-automations-view>
        `;
      case "skills":
        return html`
          <katanoid-skills-view></katanoid-skills-view>
        `;
      case "devices":
        return html`
          <katanoid-devices-view></katanoid-devices-view>
        `;
      case "integrations":
        return html`
          <katanoid-integrations-view></katanoid-integrations-view>
        `;
      case "settings":
        return html`
          <katanoid-settings-view
            .displayName=${this.user?.displayName ?? ""}
            .assistantName=${assistantName}
            .customInstructions=${this.user?.customInstructions ?? ""}
            .email=${this.user?.email ?? ""}
            .credits=${this.credits}
            .subscription=${this.subscription}
            .themeMode=${this.themeMode}
            .apiKeyHint=${this.apiKeyHint}
            .openaiKeyHint=${this.openaiKeyHint}
            .customModels=${this.customModelsList}
            .memoryEnabled=${this.user?.memoryEnabled ?? true}
            @sign-out=${this.handleSignOut}
            @theme-change=${this.handleThemeChange}
            @model-prefs-change=${this.handleModelPrefsChange}
            @tool-prefs-change=${this.handleToolPrefsChange}
            @assistant-settings-change=${this.handleAssistantSettingsChange}
            @billing-action=${this.handleBillingAction}
            @billing-cancel-downgrade=${this.handleCancelPendingDowngrade}
            @api-key-save=${this.handleApiKeySave}
            @api-key-delete=${this.handleApiKeyDelete}
            @openai-key-save=${this.handleOpenaiKeySave}
            @openai-key-delete=${this.handleOpenaiKeyDelete}
            @browse-models=${this.handleBrowseModels}
            @model-remove=${this.handleModelRemove}
            @memory-toggle=${this.handleMemoryToggle}
            @change-password=${this.handleChangePassword}
          ></katanoid-settings-view>
        `;
      default:
        return html``;
    }
  }

  private renderMobileTabBar(): TemplateResult {
    return html`
      <nav class="tab-bar">
        ${MOBILE_TABS.map(
          (tab) => html`
            <button
              class="tab-bar-item"
              aria-selected=${tab.id === "more" ? this.moreSheetOpen : this.currentView === tab.id}
              @click=${() => {
                if (tab.id === "more") {
                  this.moreSheetOpen = !this.moreSheetOpen;
                } else {
                  this.moreSheetOpen = false;
                  this.navigateTo(tab.id as ConsumerView);
                }
              }}
            >
              <span class="tab-bar-icon" .innerHTML=${tab.icon}></span>
              <span class="tab-bar-label">${tab.label}</span>
            </button>
          `
        )}
      </nav>
    `;
  }

  private renderMoreSheet(): TemplateResult {
    return html`
      <div class="more-sheet-backdrop" @click=${() => { this.moreSheetOpen = false; }}></div>
      <div class="more-sheet">
        <div class="more-sheet-handle"></div>
        ${SIDEBAR_ONLY_TABS.map(
          (tab) => html`
            <button
              class="more-sheet-item"
              aria-selected=${this.currentView === tab.id}
              @click=${() => {
                this.navigateTo(tab.id);
                this.moreSheetOpen = false;
              }}
            >
              <span class="more-sheet-icon" .innerHTML=${tab.icon}></span>
              <span class="more-sheet-label">${tab.label}</span>
            </button>
          `
        )}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "katanoid-consumer": KatanoidConsumer;
  }
}
